export interface Issue {
  item: string;
  explanation: string;
  fix?: string;
  Fix?: string;
}

const parseIssueLine = (line: string): string | Issue => {
  const cleanLine = line.trim().replace(/^\s*[•*]\s*/, '').trim();
  
  // Check for "Fix:" (case sensitive as per example, but maybe case insensitive is safer? Let's stick to Fix: as usually generated)
  const fixMatch = cleanLine.match(/(.*?)\s+Fix:\s*(.*)/);
  
  if (fixMatch) {
    const mainPart = fixMatch[1].trim();
    const fixPart = fixMatch[2].trim();
    
    // Try to split item and explanation from mainPart
    // Looking for "Title – Description" pattern (dash, en-dash, em-dash, colon)
    const separatorMatch = mainPart.match(/^(.+?)\s*([–—\-:|])\s*(.+)/);
    
    let item = '';
    let explanation = mainPart;
    
    if (separatorMatch) {
      item = separatorMatch[1].trim();
      explanation = separatorMatch[3].trim();
    }
    
    return {
      item,
      explanation,
      fix: fixPart
    };
  }
  
  return cleanLine;
};

const processIssueArray = (issues: any[] | undefined): (string | Issue)[] | undefined => {
  if (!issues || !Array.isArray(issues)) return undefined;
  
  // First, check if this is an array of alternating issues and fixes
  // The failing JSON has strings like:
  // "Issue Description..."
  // "Fix: Fix Description..."
  // We want to combine these into Issue objects
  
  const processedIssues: (string | Issue)[] = [];
  
  for (let i = 0; i < issues.length; i++) {
    const current = issues[i];
    
    if (typeof current === 'string') {
      // Check if this line is a Fix line
      const isFixLine = current.trim().startsWith('Fix:');
      
      if (isFixLine) {
        // If it's a fix line, try to attach it to the previous issue
        if (processedIssues.length > 0) {
          const lastIssue = processedIssues[processedIssues.length - 1];
          
          if (typeof lastIssue === 'string') {
            // Convert previous string issue to Issue object
            const fixContent = current.trim().substring(4).trim(); // Remove "Fix:"
            
            // Parse the previous line to get item and explanation
            const parsedPrevious = parseIssueLine(lastIssue);
            
            if (typeof parsedPrevious === 'string') {
               // If parseIssueLine returned a string, it didn't find a Fix in it
               // So we create a new Issue object from it
               // Try to split item/explanation again
               const separatorMatch = parsedPrevious.match(/^(.+?)\s*([–—\-:|])\s*(.+)/);
               let item = '';
               let explanation = parsedPrevious;
               
               if (separatorMatch) {
                 item = separatorMatch[1].trim();
                 explanation = separatorMatch[3].trim();
               }
               
               processedIssues[processedIssues.length - 1] = {
                 item,
                 explanation,
                 fix: fixContent
               };
            } else {
              // It's already an Issue object (unlikely if it was just a string, but possible via parseIssueLine)
              // Just add the fix to it
              processedIssues[processedIssues.length - 1] = {
                ...parsedPrevious,
                fix: fixContent
              };
            }
          } else {
             // Previous item is already an Issue object, just add/overwrite fix
             processedIssues[processedIssues.length - 1] = {
               ...lastIssue,
               fix: current.trim().substring(4).trim()
             };
          }
        } else {
          // Fix line at start? Treat as regular string
          processedIssues.push(parseIssueLine(current));
        }
      } else {
        // Not a fix line, just add it (will process via parseIssueLine to handle inline fixes if any)
        processedIssues.push(parseIssueLine(current));
      }
    } else if (typeof current === 'object' && current !== null) {
      // Explicitly map object to Issue interface to ensure structure
      // Handle both "Fix" and "fix" keys, and ensure item/explanation are present
      const issueObj: Issue = {
        item: current.item || '',
        explanation: current.explanation || '',
        fix: current.fix || current.Fix || undefined,
        Fix: current.Fix || current.fix || undefined // Keep both populated just in case
      };
      processedIssues.push(issueObj);
    }
  }
  
  return processedIssues;
};

export interface Tier1Fundamental {
  heading: string;
  observation: string;
}

export interface ContractAnalysis {
  summary?: string;
  redIssues?: (string | Issue)[];
  yellowIssues?: (string | Issue)[];
  greenIssues?: (string | Issue)[];
  tier1Fundamentals?: Tier1Fundamental[];
  overallEvaluation?: string;
  remainingUncertainties?: string[];
  isStructured?: boolean;
  isJson?: boolean;
}

const extractIssuesFromMalformedJson = (jsonContent: string, sectionKey: string): Issue[] => {
  const issues: Issue[] = [];
  
  // Find the section start: "Key": [
  const sectionRegex = new RegExp(`"${sectionKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\s*:\\s*\\[`);
  const match = jsonContent.match(sectionRegex);
  
  if (!match) return [];
  
  const startIndex = match.index! + match[0].length;
  
  // Find the array content using bracket counting
  let bracketCount = 1;
  let endIndex = -1;
  let inString = false;
  
  for (let i = startIndex; i < jsonContent.length; i++) {
    const char = jsonContent[i];
    if (char === '"' && jsonContent[i-1] !== '\\') inString = !inString;
    if (!inString) {
      if (char === '[') bracketCount++;
      if (char === ']') {
        bracketCount--;
        if (bracketCount === 0) {
          endIndex = i;
          break;
        }
      }
    }
  }
  
  if (endIndex === -1) endIndex = jsonContent.length; // Truncated?
  
  const arrayContent = jsonContent.substring(startIndex, endIndex);
  
  // Split by "}," to separate objects roughly
  // This is a heuristic and might fail if strings contain "},"
  const rawObjects = arrayContent.split(/}\s*,\s*{|{\s*|\s*}/).filter(s => s.trim().length > 10);
  
  for (const rawObj of rawObjects) {
    // Extract fields from rawObj
    const itemMatch = rawObj.match(/"item":\s*"((?:[^"\\]|\\.)*)"/);
    const explanationMatch = rawObj.match(/"explanation":\s*"((?:[^"\\]|\\.)*)"/);
    const fixMatch = rawObj.match(/"(?:Fix|fix)":\s*"((?:[^"\\]|\\.)*)"/);
    
    if (itemMatch && explanationMatch) {
      issues.push({
        item: itemMatch[1].replace(/\\"/g, '"'),
        explanation: explanationMatch[1].replace(/\\"/g, '"'),
        fix: fixMatch ? fixMatch[1].replace(/\\"/g, '"') : undefined
      });
    }
  }
  
  return issues;
};

const extractFundamentalsFromMalformedJson = (jsonContent: string): Tier1Fundamental[] => {
  const fundamentals: Tier1Fundamental[] = [];
  const sectionKey = "Tier-1 Fundamentals";
  
  const sectionRegex = new RegExp(`"${sectionKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\s*:\\s*\\[`);
  const match = jsonContent.match(sectionRegex);
  
  if (!match) return [];
  
  const startIndex = match.index! + match[0].length;
  let bracketCount = 1;
  let endIndex = -1;
  let inString = false;
  
  for (let i = startIndex; i < jsonContent.length; i++) {
    const char = jsonContent[i];
    if (char === '"' && jsonContent[i-1] !== '\\') inString = !inString;
    if (!inString) {
      if (char === '[') bracketCount++;
      if (char === ']') {
        bracketCount--;
        if (bracketCount === 0) {
          endIndex = i;
          break;
        }
      }
    }
  }
  
  if (endIndex === -1) endIndex = jsonContent.length;
  
  const arrayContent = jsonContent.substring(startIndex, endIndex);
  const rawObjects = arrayContent.split(/}\s*,\s*{|{\s*|\s*}/).filter(s => s.trim().length > 10);
  
  for (const rawObj of rawObjects) {
    const headingMatch = rawObj.match(/"heading":\s*"((?:[^"\\]|\\.)*)"/);
    const observationMatch = rawObj.match(/"observation":\s*"((?:[^"\\]|\\.)*)"/);
    
    if (headingMatch && observationMatch) {
      fundamentals.push({
        heading: headingMatch[1].replace(/\\"/g, '"'),
        observation: observationMatch[1].replace(/\\"/g, '"')
      });
    }
  }
  
  return fundamentals;
};

export const parseResponse = (content: string): ContractAnalysis => {
  // Try to parse as JSON first
  try {
    // Look for JSON in the response (it might be wrapped in markdown code blocks)
    let jsonContent = content;
    // Match any code block, not just json, and be more permissive with whitespace
    const jsonMatch = content.match(/```[\w]*\s*([\s\S]+?)\s*```/) || content.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      jsonContent = jsonMatch[1] || jsonMatch[0];
    }

    // Clean up common JSON issues
    // 1. Remove trailing commas before closing braces/brackets
    jsonContent = jsonContent.replace(/,(\s*[}\]])/g, '$1');
    // 2. Fix potential unescaped newlines in strings (basic attempt)
    // jsonContent = jsonContent.replace(/(?<!\\)\n/g, '\\n'); // This is risky if it replaces newlines in formatting

    const data = JSON.parse(jsonContent);
    const finalAnswer = data["Final Answer"] || data["FinalAnswer"] || data;

    if (finalAnswer) {
      const rawSummary = finalAnswer.Summary || finalAnswer.summary;
      let summary: string | undefined;
      if (typeof rawSummary === 'object' && rawSummary !== null) {
        const parts: string[] = [];
        if (rawSummary.total_pages) parts.push(`Total Pages: ${rawSummary.total_pages}`);
        if (rawSummary.assessment) parts.push(rawSummary.assessment);
        summary = parts.join('\n\n');
      } else {
        summary = rawSummary;
      }

      return {
        summary,
        redIssues: processIssueArray(
          finalAnswer["Red (Highly Problematic Issues)"] || finalAnswer.redIssues || finalAnswer.red_issues || finalAnswer.Red
        ),
        yellowIssues: processIssueArray(
          finalAnswer["Yellow (Potential Long-Term Problems)"] || finalAnswer.yellowIssues || finalAnswer.yellow_issues || finalAnswer.Yellow
        ),
        greenIssues: processIssueArray(
          finalAnswer["Green (Negotiable / Minor Points)"] || finalAnswer.greenIssues || finalAnswer.green_issues || finalAnswer.Green
        ),
        tier1Fundamentals: finalAnswer["Tier-1 Fundamentals"] || finalAnswer.tier1Fundamentals || finalAnswer.tier1_fundamentals,
        overallEvaluation: finalAnswer["Overall Evaluation"] || finalAnswer.overallEvaluation || finalAnswer.overall_evaluation || finalAnswer.OverallEvaluation,
        remainingUncertainties: finalAnswer["Remaining Uncertainties"] || finalAnswer.remainingUncertainties || finalAnswer.remaining_uncertainties || finalAnswer.RemainingUncertainties,
        isStructured: true,
        isJson: true
      };
    }
  } catch (e) {
    console.warn('JSON parsing failed, attempting fuzzy extraction', e);
    // If JSON parsing fails, try to extract fields using regex from the JSON-like string
    // This helps when the JSON is slightly malformed (e.g. truncated, missing quotes)
    
    try {
      const summaryMatch = content.match(/"Summary":\s*"((?:[^"\\]|\\.)*)"/);
      const overallMatch = content.match(/"Overall Evaluation":\s*"((?:[^"\\]|\\.)*)"/);
      
      // If we can at least find the summary, we can try to construct a partial result
      if (summaryMatch) {
         return {
           summary: summaryMatch[1].replace(/\\"/g, '"'), // Unescape quotes
           overallEvaluation: overallMatch ? overallMatch[1].replace(/\\"/g, '"') : undefined,
           redIssues: extractIssuesFromMalformedJson(content, "Red (Highly Problematic Issues)"),
           yellowIssues: extractIssuesFromMalformedJson(content, "Yellow (Potential Long-Term Problems)"),
           greenIssues: extractIssuesFromMalformedJson(content, "Green (Negotiable / Minor Points)"),
           tier1Fundamentals: extractFundamentalsFromMalformedJson(content),
           // Remaining Uncertainties is an array of strings, simpler to extract if needed
           isStructured: true,
           isJson: true
         };
      }
    } catch (err) {
      // Ignore fuzzy extraction errors
    }
  }

  // Check if the response follows the expected format
  const hasExpectedSections = content.includes('Final Answer:') && 
                             content.includes('Summary:') &&
                             content.includes('Red (Highly Problematic Issues):') &&
                             content.includes('Yellow (Potential Long-Term Problems):') &&
                             /Green \(Negotiable[^:]*\):/.test(content) &&
                             content.includes('Overall Evaluation:') &&
                             content.includes('Remaining Uncertainties:');

  if (!hasExpectedSections) {
    return {
      summary: content,
      isStructured: false
    };
  }

  const analysis: ContractAnalysis = {
    isStructured: true,
    isJson: false
  };

  // Extract summary (appears after "Summary:" and before "Red (Highly Problematic Issues):")
  const summaryMatch = content.match(/Summary:\s*([\s\S]+?)(?=Red \(Highly Problematic Issues\):|$)/);
  if (summaryMatch) {
    analysis.summary = summaryMatch[1].trim();
  }

  // Split content into sections based on headers
  const sections = content.split(/(?=Red \(Highly Problematic Issues\):|Yellow \(Potential Long-Term Problems\):|Green \(Negotiable[^:]*\):|Overall Evaluation:|Remaining Uncertainties:)/);

  sections.forEach(section => {
    const trimmedSection = section.trim();
    
    if (trimmedSection.startsWith('Red (Highly Problematic Issues):')) {
      const redContent = trimmedSection.replace('Red (Highly Problematic Issues):', '').trim();
      analysis.redIssues = redContent
        .split('\n')
        .filter(line => line.trim().length > 0 && (line.includes('•') || line.includes('*')))
        .map(line => parseIssueLine(line))
        .filter((line: string | Issue) => typeof line === 'string' ? line.length > 0 : true);
    } else if (trimmedSection.startsWith('Yellow (Potential Long-Term Problems):')) {
      const yellowContent = trimmedSection.replace('Yellow (Potential Long-Term Problems):', '').trim();
      analysis.yellowIssues = yellowContent
        .split('\n')
        .filter(line => line.trim().length > 0 && (line.includes('•') || line.includes('*')))
        .map(line => parseIssueLine(line))
        .filter((line: string | Issue) => typeof line === 'string' ? line.length > 0 : true);
    } else if (trimmedSection.match(/^Green \(Negotiable[^:]*\):/)) {
      const greenContent = trimmedSection.replace(/^Green \(Negotiable[^:]*\):/, '').trim();
      analysis.greenIssues = greenContent
        .split('\n')
        .filter(line => line.trim().length > 0 && (line.includes('•') || line.includes('*')))
        .map(line => parseIssueLine(line))
        .filter((line: string | Issue) => typeof line === 'string' ? line.length > 0 : true);
    } else if (trimmedSection.startsWith('Overall Evaluation:')) {
      analysis.overallEvaluation = trimmedSection.replace('Overall Evaluation:', '').trim();
    } else if (trimmedSection.startsWith('Remaining Uncertainties:')) {
      const uncertaintiesContent = trimmedSection.replace('Remaining Uncertainties:', '').trim();
      analysis.remainingUncertainties = uncertaintiesContent
        .split('\n')
        .filter(line => line.trim().length > 0 && (line.includes('•') || line.includes('*')))
        .map(line => line.trim().replace(/^\s*[•*]\s*/, '').trim())
        .filter(line => line.length > 0); // Remove empty lines
    }
  });

  return analysis;
}; 